import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

// Hook to fetch FE Base list for dropdown
const useFetchFeBaseList = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then(res => setList(res.data))
      .catch(() => setList([]));
  }, []);
  return list;
};

export const AdminFePropertyHoldingsComponent = () => {
  const feBaseList = useFetchFeBaseList();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    financial_establishment: '',
    propery_name: '',
    property_location: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [uploading, setUploading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'financial_establishment', field: 'financial_establishment', headerName: 'FE Base', width: 200 },
    { id: 'propery_name', field: 'propery_name', headerName: 'Property Name', width: 200 },
    { id: 'property_location', field: 'property_location', headerName: 'Property Location', width: 300 },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  const fetchPropertyHoldings = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-property-holdings/`)
      .then(response => {
        const apiData = response.data.map((item, index) => {
          // Find FE Base object by id to get FE name
          const feBaseObj = feBaseList.find(fe => fe.id === (item.financial_establishment?.id || item.financial_establishment));
          return {
            id: item.id,
            slNo: index + 1,
            financial_establishment: feBaseObj ? feBaseObj.name_of_fe : (item.financial_establishment?.name_of_fe || item.financial_establishment),
            propery_name: item.propery_name,
            property_location: item.property_location,
          };
        });
        setRows(apiData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Failed to fetch Property Holdings:', error);
        setSnackbarMessage('Failed to load Property Holdings.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (feBaseList.length > 0) {
      fetchPropertyHoldings();
    }
  }, [feBaseList]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAddItem = () => {
    setIsEditMode(false);
    setFormData({
      financial_establishment: '',
      propery_name: '',
      property_location: '',
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);

    // Find FE Base by name or by UUID to set correct id for form dropdown
    const feBaseObj = feBaseList.find(fe => fe.name_of_fe === item.financial_establishment || fe.id === item.financial_establishment);

    setFormData({
      financial_establishment: feBaseObj ? feBaseObj.id : '',
      propery_name: item.propery_name,
      property_location: item.property_location,
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/fe-mgmt/fe-property-holdings/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('Property Holding deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchPropertyHoldings();
      })
      .catch(error => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete Property Holding.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.financial_establishment) errors.financial_establishment = 'FE Base is required';
    if (!formData.propery_name?.trim()) errors.propery_name = 'Property name is required';
    if (!formData.property_location?.trim()) errors.property_location = 'Property location is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    const payload = {
      financial_establishment: formData.financial_establishment,
      propery_name: formData.propery_name,
      property_location: formData.property_location,
    };
    try {
      setUploading(true);
      if (isEditMode) {
        await api.patch(`/fe-mgmt/fe-property-holdings/${selectedItem.id}/`, payload);
        setSnackbarMessage('Property Holding updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-property-holdings/`, payload);
        setSnackbarMessage('Property Holding added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchPropertyHoldings();
      setOpenFormDialog(false);
    } catch (error) {
      console.error('Submit failed:', error);
      setSnackbarMessage('Failed to submit Property Holding.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center"
        bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Property Holdings
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>
          Add
        </Button>
      </Box>
      <TableComponent columns={columns} rows={rows} manageColumnStatus enableSort
        sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}} />
      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Property Holding' : 'Add Property Holding'}</DialogTitle>
        <DialogContent>
          <TextField select margin="dense" label="FE Base" fullWidth
            value={formData.financial_establishment}
            error={!!formErrors.financial_establishment}
            helperText={formErrors.financial_establishment}
            onChange={e => setFormData({ ...formData, financial_establishment: e.target.value })}
            disabled={uploading}>
            {feBaseList.map(fe => <MenuItem key={fe.id} value={fe.id}>{fe.name_of_fe || fe.id}</MenuItem>)}
          </TextField>
          <TextField margin="dense" label="Property Name" fullWidth
            value={formData.propery_name}
            error={!!formErrors.propery_name}
            helperText={formErrors.propery_name}
            onChange={e => setFormData({ ...formData, propery_name: e.target.value })}
            disabled={uploading} />
          <TextField margin="dense" label="Property Location" fullWidth multiline minRows={3}
            value={formData.property_location}
            error={!!formErrors.property_location}
            helperText={formErrors.property_location}
            onChange={e => setFormData({ ...formData, property_location: e.target.value })}
            disabled={uploading} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>
            {isEditMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this Property Holding?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>Delete</Button>
        </DialogActions>
      </Dialog>
      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
